/**
 * ListToolbar
 *
 * One flex row (wrapping), every slot independently omittable. Fixed order — do not
 * reorder or relocate these slots:
 *  - Left: primary action(s), then secondary actions (appearance="secondary").
 *  - Right: FK filter dropdowns (<=2) -> SearchBox -> "Show Deleted" Switch ->
 *    extraFilters (optional bespoke controls) -> column-filters toggle -> Refresh.
 * See `.github/context/list-grid-standard.context.md` §5-6.
 */
import type { FC, ReactNode } from 'react';
import {
  Button,
  SearchBox,
  Switch,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwise24Regular, Filter24Filled, Filter24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  left: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  right: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginLeft: 'auto',
  },
  fkGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
});

export interface ListToolbarProps {
  /** Primary action(s) — e.g. `[+ New <Entity>]`, then bulk actions. */
  primaryActions?: ReactNode;
  /** Non-primary buttons (e.g. navigation to related data) rendered after the primary action(s). */
  secondaryActions?: ReactNode;
  /** Up to 2 FK filter dropdowns, rendered leftmost in the right-hand slot group. */
  fkFilters?: ReactNode[];
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showDeleted?: boolean;
  onShowDeletedChange?: (value: boolean) => void;
  /** Override the switch label — e.g. "Show Revoked" for an entity that isn't soft-deleted. */
  showDeletedLabel?: string;
  showDeletedDisabled?: boolean;
  showDeletedDisabledReason?: string;
  filtersVisible?: boolean;
  onToggleFilters?: () => void;
  hasActiveFilters?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  testIdPrefix?: string;
  /**
   * Bespoke filter controls beyond the ≤2 `fkFilters` dropdowns — e.g. a date-range picker,
   * a multi-select, or any control that doesn't fit the FK-dropdown shape. Rendered in the
   * right-hand slot group, after the "Show Deleted" switch and before the column-filter
   * toggle. Omit for no change in behaviour.
   */
  extraFilters?: ReactNode;
}

export const ListToolbar: FC<ListToolbarProps> = ({
  primaryActions,
  secondaryActions,
  fkFilters,
  search,
  onSearchChange,
  searchPlaceholder = 'Search…',
  showDeleted,
  onShowDeletedChange,
  showDeletedLabel = 'Show Deleted',
  showDeletedDisabled,
  showDeletedDisabledReason,
  filtersVisible,
  onToggleFilters,
  hasActiveFilters,
  onRefresh,
  isRefreshing,
  testIdPrefix,
  extraFilters,
}) => {
  const styles = useStyles();
  const testId = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);

  const showDeletedSwitch = onShowDeletedChange && (
    <Switch
      checked={Boolean(showDeleted)}
      onChange={(_, data) => onShowDeletedChange(data.checked)}
      label={showDeletedLabel}
      disabled={showDeletedDisabled}
      data-testid={testId('show-deleted')}
    />
  );

  return (
    <div className={styles.root}>
      {(primaryActions || secondaryActions) && (
        <div className={styles.left}>
          {primaryActions}
          {secondaryActions}
        </div>
      )}

      <div className={styles.right}>
        {fkFilters && fkFilters.length > 0 && (
          <div className={styles.fkGroup}>{fkFilters.slice(0, 2)}</div>
        )}

        {onSearchChange && (
          <SearchBox
            value={search ?? ''}
            onChange={(_, data) => onSearchChange(data.value)}
            placeholder={searchPlaceholder}
            aria-label="Search"
            data-testid={testId('search')}
          />
        )}

        {onShowDeletedChange &&
          (showDeletedDisabled && showDeletedDisabledReason ? (
            <Tooltip content={showDeletedDisabledReason} relationship="label">
              <span>{showDeletedSwitch}</span>
            </Tooltip>
          ) : (
            showDeletedSwitch
          ))}

        {extraFilters}

        {onToggleFilters && (
          <Button
            appearance={filtersVisible ? 'primary' : 'secondary'}
            icon={hasActiveFilters ? <Filter24Filled /> : <Filter24Regular />}
            onClick={onToggleFilters}
            aria-label="Toggle column filters"
            data-testid={testId('filter-toggle')}
          />
        )}

        {onRefresh && (
          <Button
            appearance="subtle"
            icon={<ArrowClockwise24Regular />}
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label="Refresh"
            data-testid={testId('refresh')}
          />
        )}
      </div>
    </div>
  );
};
